import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Instagram,
  Linkedin,
  Facebook,
  Music2,
  Calendar,
  ArrowRight,
  MessageCircle
} from 'lucide-react';
import { whatsappHref } from '../utils/gtag';
import BookingForm from '../components/BookingForm';
import AfsfhSeal from '../components/AfsfhSeal';
import CTA from '../components/CTA';

const Contact: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Contact Sharon Clare Hypnotherapy | Book Consultation Belfast, Lisburn & Online</title>
        <meta
          name="description"
          content="Book your free 10-minute consultation with Sharon Clare Hypnotherapy. Clinics: Belfast Wellness Clinic, 228 Ormeau Road, Belfast & The Arzen Collective, 13B Antrim Street, Lisburn. Phone: 07932 139245. In-person and online sessions available across Northern Ireland."
        />
        <meta name="keywords" content="book hypnotherapy Belfast, book hypnotherapy Lisburn, hypnotherapist Belfast, hypnotherapist Coleraine, schedule hypnotherapy appointment, Belfast hypnotherapy clinic, Lisburn hypnotherapy clinic, online hypnotherapy, Northern Ireland hypnotherapy" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/contact/" />
        <meta property="og:title" content="Contact Sharon Clare Hypnotherapy | Book Consultation Belfast, Lisburn & Online" />
        <meta property="og:description" content="Book your free 10-minute consultation with Sharon Clare Hypnotherapy. Clinics in Belfast & Lisburn + online sessions." />
        <meta property="og:url" content="https://www.sharonclare-hypnotherapy.com/contact/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact Sharon Clare Hypnotherapy | Book Consultation Belfast, Lisburn & Online" />
        <meta name="twitter:description" content="Book your free 10-minute consultation. Clinics: Belfast & Lisburn + online. Phone: 07932 139245." />
      </Helmet>
      
      <main>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Get In Touch
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              Ready to start your transformation? Book your <a href="/services" className="text-accent-600 hover:text-accent-700 underline">free consultation</a> or get in touch 
              with any questions. I'm here to help you on your journey to better mental health with specialized support for <a href="/anxiety-stress" className="text-accent-600 hover:text-accent-700 underline">anxiety</a>, <a href="/persistent-pain" className="text-accent-600 hover:text-accent-700 underline">chronic pain</a>, and more.
              Available in Belfast, Lisburn and online.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#booking"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Request a 10-Minute Call
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Request a 10-Minute Call
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Take the first step towards healing and well-being. Request a 10-minute call 
              to discuss your goals and learn how hypnotherapy can help you.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <BookingForm />
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Contact Information
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple ways to get in touch. Choose what works best for you.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <div className="space-y-8">
              {/* Practice Locations */}
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-6">
                  Practice Locations
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Belfast Clinic</h4>
                      <p className="text-gray-600 mb-2">
                        Belfast Wellness Clinic, 228 Ormeau Road<br />
                        Belfast, BT7 2FZ
                      </p>
                      <div className="text-sm text-gray-500 space-y-1">
                        <p>Clinic days and times vary, and early evening appointments are available.</p>
                        <a
                          href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-accent-600 hover:text-accent-700 underline"
                        >
                          Check current availability and book
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Lisburn Clinic</h4>
                      <p className="text-gray-600 mb-2">
                        The Arzen Collective, 13B Antrim Street<br />
                        Lisburn, BT28
                      </p>
                      <div className="text-sm text-gray-500 space-y-1">
                        <p>Appointment times vary, and early evening appointments are available.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Online Sessions</h4>
                      <p className="text-gray-600">
                        Virtual hypnotherapy sessions<br />
                        Available by arrangement<br />
                        Secure video platform
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-6">
                  Get In Touch
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <Phone className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                      <a 
                        href="tel:07932139245"
                        className="text-accent-600 hover:text-accent-700 font-medium text-lg"
                      >
                        07932 139245
                      </a>
                      <p className="text-gray-600 text-sm mt-1">
                        Available Monday-Friday 9am-6pm, Saturday 10am-4pm
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <MessageCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">WhatsApp</h4>
                      <a
                        href={whatsappHref("Hi Sharon, I'd like to ask about hypnotherapy.")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-600 hover:text-accent-700 font-medium text-lg"
                      >
                        Message me on WhatsApp
                      </a>
                      <p className="text-gray-600 text-sm mt-1">
                        Often the quickest way to reach me
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Mail className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                      <a 
                        href="mailto:Sharon@sharonclare-hypnotherapy.com"
                        className="text-accent-600 hover:text-accent-700 font-medium"
                      >
                        Sharon@sharonclare-hypnotherapy.com
                      </a>
                      <p className="text-gray-600 text-sm mt-1">
                        I typically respond within 24 hours
                      </p>
                    </div>
                  </div>


                  <div className="flex items-start space-x-4">
                    <Clock className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Opening Hours</h4>
                      <div className="text-gray-600 space-y-1">
                        <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                        <p>Saturday: 10:00 AM - 4:00 PM</p>
                        <p>Sunday: Closed</p>
                        <p className="text-sm text-accent-600 mt-2">
                          Evening appointments available by request
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-6">
                  Follow My Journey
                </h3>
                
                <div className="space-y-4">
                  <a 
                    href="https://instagram.com/sharonclarehypnotherapy" 
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center space-x-3 text-gray-700 hover:text-accent-600 transition-colors group"
                  >
                    <Instagram className="w-6 h-6 text-accent-600 group-hover:text-accent-700" />
                    <div>
                      <p className="font-medium">@sharonclarehypnotherapy</p>
                      <p className="text-sm text-gray-500">Tips, insights & success stories</p>
                    </div>
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/sharonclare-hypnotherapy" 
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center space-x-3 text-gray-700 hover:text-accent-600 transition-colors group"
                  >
                    <Linkedin className="w-5 h-5 text-accent-600 group-hover:text-accent-700" />
                    <div>
                      <p className="font-medium">LinkedIn</p>
                    </div>
                  </a>
                  <a 
                    href="https://www.facebook.com/SharonClareHypnotherapy/" 
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center space-x-3 text-gray-700 hover:text-accent-600 transition-colors group"
                  >
                    <Facebook className="w-5 h-5 text-accent-600 group-hover:text-accent-700" />
                    <div>
                      <p className="font-medium">SharonClareHypnotherapy</p>
                    </div>
                  </a>
                  <a 
                    href="https://www.tiktok.com/@sharonclarehypnot" 
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center space-x-3 text-gray-700 hover:text-accent-600 transition-colors group"
                  >
                    <Music2 className="w-5 h-5 text-accent-600 group-hover:text-accent-700" />
                    <div>
                      <p className="font-medium">@sharonclarehypnot</p>
                    </div>
                  </a>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600 mb-4">
                      Stay updated with helpful articles, client success stories, and insights 
                      into the transformative power of hypnotherapy.
                    </p>
                    <a 
                      href="https://www.brainzmagazine.com/executive-contributor/sharon-clare" 
                      className="inline-flex items-center text-accent-600 hover:text-accent-700 font-medium transition-colors"
                    >
                      Read My Brainz Articles
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200 mt-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Professional Membership</h4>
                  <AfsfhSeal />
                </div>
              </div>

              {/* Links Section */}
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-6">
                  Links
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Clinics */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Clinics & Partners</h4>
                    <ul className="space-y-2 text-accent-600">
                      <li>Belfast Wellness Clinic, 228 Ormeau Road, Belfast</li>
                      <li>The Arzen Collective, 13B Antrim Street, Lisburn</li>
                    </ul>
                  </div>

                  {/* Profiles */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Profiles & Articles</h4>
                    <ul className="space-y-2 text-accent-600">
                      <li><a href="https://instagram.com/sharonclarehypnotherapy" target="_blank" rel="noopener noreferrer" className="hover:text-accent-700">Instagram</a></li>
                      <li><a href="https://www.linkedin.com/in/sharonclare-hypnotherapy" target="_blank" rel="noopener noreferrer" className="hover:text-accent-700">LinkedIn</a></li>
                      <li><a href="https://www.facebook.com/SharonClareHypnotherapy/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-700">Facebook</a></li>
                      <li><a href="https://www.tiktok.com/@sharonclarehypnot" target="_blank" rel="noopener noreferrer" className="hover:text-accent-700">TikTok</a></li>
                      <li><a href="https://www.brainzmagazine.com/executive-contributor/sharon-clare" target="_blank" rel="noopener noreferrer" className="hover:text-accent-700">Brainz Magazine Articles</a></li>
                      <li><a href="https://www.hypnotherapy-directory.org.uk/hypnotherapists/sharon-clare" target="_blank" rel="noopener noreferrer" className="hover:text-accent-700">Hypnotherapy Directory</a></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Map & Additional Info */}
            <div className="space-y-8">
              {/* Google Maps */}
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-6">
                  Practice Locations in Northern Ireland
                </h3>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2316.123456789!2d-5.8234!3d54.6369!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48610bfe73404a21%3A0x9f225408e6a03c32!2sBelfast%20Chiropractic%20Clinic%2C%20228%20Ormeau%20Rd%2C%20Belfast%20BT7%202FZ%2C%20UK!5e0!3m2!1sen!2suk!4v1758217816261!5m2!1sen!2suk" 
                    width="100%" 
                    height="450" 
                    style={{border: 0}} 
                    allowFullScreen 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Sharon Clare Hypnotherapy Practice Locations - Northern Ireland"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-4">
                  <strong>Belfast clinic, south Belfast:</strong> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ (days and times vary).<br />
                  <strong>Lisburn clinic:</strong> The Arzen Collective, 13B Antrim Street, Lisburn BT28 (appointment times vary).<br />
                  Online sessions also available across Northern Ireland.
                </p>
              </div>

              {/* Emergency Contact */}
              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8 border border-red-200">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Need Immediate Support?
                </h3>
                <p className="text-gray-600 mb-4">
                  If you're experiencing a mental health crisis, please contact emergency services or a crisis helpline immediately.
                </p>
                <div className="space-y-2 text-sm">
                  <p><strong>Emergency:</strong> 999</p>
                  <p><strong>Samaritans:</strong> 116 123</p>
                  <p><strong>Lifeline:</strong> 0808 808 8000</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Ready to Start Your Transformation?"
        subtitle="Don't wait to take control of your mental health. Book your free 10-minute consultation today."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="#booking"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
    </>
  );
};

export default Contact;
